import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtINRFull, fmtDate } from '@/lib/format';
import { AlertTriangle, ExternalLink } from 'lucide-react';

/**
 * Iter42 — In-app Accounts alert area.
 *
 * Renders a visible alert panel for chargeable or over-allowance visits.
 * Only shown to Accounts and Owner (server also enforces this on
 * GET /api/visits/quota_alerts).
 *
 * NOTE: This is an ALERT ONLY — no approval / invoicing / payment /
 * accounts workflow buttons. Users can only click through to the linked
 * project or work order for context.
 */
export function AccountsAlertPanel({ compact = false, className = '' }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [ready, setReady] = useState(false);

  const isRecipient = user?.role === 'owner' || user?.role === 'accounts';

  useEffect(() => {
    if (!isRecipient) { setReady(true); return; }
    let alive = true;
    api.get('/visits/quota_alerts')
      .then(r => { if (alive) { setItems(r.data?.items || []); setCount(r.data?.count || 0); setReady(true); } })
      .catch(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [isRecipient]);

  if (!isRecipient || !ready) return null;
  if (count === 0) return null;

  const show = compact ? items.slice(0, 3) : items;

  return (
    <div className={`border rounded-md ${className}`}
         style={{ borderColor: 'hsl(38 70% 60%)', background: 'hsl(48 96% 96%)' }}
         data-testid="accounts-alert-panel">
      <div className="px-3 py-2 flex items-center justify-between"
           style={{ borderBottom: '1px solid hsl(38 70% 82%)' }}>
        <div className="flex items-center gap-2 text-[13px] font-medium text-[hsl(38_80%_30%)]">
          <AlertTriangle size={13} />
          Accounts alert — {count} visit{count === 1 ? '' : 's'} outside included allowance
        </div>
        <div className="text-[10.5px] text-[hsl(38_60%_40%)] uppercase tracking-wider">
          Alert only · no action needed
        </div>
      </div>
      <ul className="divide-y" style={{ borderColor: 'hsl(38 60% 88%)' }}>
        {show.map(it => (
          <li key={it.visit_id}
              className="px-3 py-2 text-[12.5px] flex items-center justify-between gap-3"
              data-testid={`accounts-alert-row-${it.visit_id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">
                <Link to={`/projects/${it.project_id}`} className="hover:underline">
                  {it.project_name}
                </Link>
                {it.work_order?.wo_number && (
                  <span className="ml-2 chip text-[10px] font-mono">{it.work_order.wo_number}</span>
                )}
              </div>
              <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">
                Visit {fmtDate(it.visit_date)}
                {it.work_order?.basis && <> · {it.work_order.basis.replace(/_/g, ' ')}</>}
                {' · '} status: {it.status.replace(/_/g, ' ')}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="tabular-nums font-medium">
                {it.billable_value_inr != null ? fmtINRFull(it.billable_value_inr) : '—'}
              </div>
              <Link to={`/visits`}
                    className="text-[11px] text-[hsl(var(--primary))] hover:underline inline-flex items-center gap-1"
                    data-testid={`accounts-alert-open-${it.visit_id}`}>
                Open <ExternalLink size={9} />
              </Link>
            </div>
          </li>
        ))}
      </ul>
      {compact && items.length > 3 && (
        <div className="px-3 py-1.5 text-[11.5px] text-[hsl(38_70%_35%)] text-center border-t"
             style={{ borderColor: 'hsl(38 60% 88%)' }}>
          + {items.length - 3} more — <Link to="/visits" className="underline">see all</Link>
        </div>
      )}
    </div>
  );
}
